/**
 * FiveHeadComplaintTest - Accordion-based display of the FCA DISP 1.3.1R complaint test
 *
 * Atomic level: Organism
 * Composes: Accordion (shadcn), Button, IconWrapper
 *
 * Displays the five regulatory heads for complaint detection with expandable
 * sections showing evidence/explanation for each head's status.
 *
 * Following GO UI Design System patterns.
 */

import React from 'react';
import { cn } from '../../../../lib/utils';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../../ui/accordion';
import { Button } from '../../../ui/button';
import IconWrapper from '../../../ui/IconWrapper';
import { FiCheck, FiX, FiAlertCircle } from 'react-icons/fi';
import type { EligibleComplaintDetection, FiveHeadsCriterion, DetectionStatus } from '../../../../types/record';

interface FiveHeadComplaintTestProps {
  detection: EligibleComplaintDetection;
  isConfirmed?: boolean;
  onConfirm?: () => void;
  onReject?: () => void;
  className?: string;
  /** Start with accordion collapsed (default: true) */
  startCollapsed?: boolean;
}

/**
 * Head titles mapping - matches the FCA DISP 1.3.1R framework
 */
const HEAD_TITLES: Record<string, string> = {
  expression_of_dissatisfaction: 'Expression of Dissatisfaction',
  about_financial_service: 'About a Financial Service',
  harm_alleged_or_likely: 'Harm Alleged or Likely',
  addressed_to_firm: 'Addressed to the Firm',
  eligible_complainant: 'Eligible Complainant',
};

/**
 * Get status icon and colors
 */
const getStatusDisplay = (status: DetectionStatus) => {
  switch (status) {
    case 'detected':
      return {
        icon: FiCheck,
        bgColor: 'bg-[var(--go-success)]/10',
        iconColor: 'text-[var(--go-success)]',
        borderColor: 'border-[var(--go-success)]/20',
      };
    case 'not_detected':
      return {
        icon: FiX,
        bgColor: 'bg-[var(--go-bittersweet)]/10',
        iconColor: 'text-[var(--go-bittersweet)]',
        borderColor: 'border-[var(--go-bittersweet)]/20',
      };
    case 'uncertain':
    default:
      return {
        icon: FiAlertCircle,
        bgColor: 'bg-[var(--go-warning)]/10',
        iconColor: 'text-[var(--go-warning)]',
        borderColor: 'border-[var(--go-warning)]/20',
      };
  }
};

/**
 * Get outcome summary styling
 */
const getOutcomeSummary = (outcome: string, headsCount: number, confidence: number) => {
  // Handle confidence: if > 1, it's already 0-100 scale; if <= 1, it's 0-1 scale
  const normalizedConfidence = confidence > 1 ? confidence : confidence * 100;
  const confidencePct = `${normalizedConfidence.toFixed(1)}%`;
  const allSatisfied = outcome === 'complaint_disp';

  if (allSatisfied) {
    return {
      bgColor: 'bg-[var(--go-teal)]/10',
      borderColor: 'border-[var(--go-teal)]/20',
      textColor: 'text-[var(--go-teal)]',
      icon: FiCheck,
      iconBg: 'bg-[var(--go-teal)]',
      message: `All ${headsCount} Heads Satisfied: This interaction meets the full definition of a Regulated Complaint under FCA DISP 1.3.1R.`,
      confidence: confidencePct,
    };
  }

  if (outcome === 'dissatisfaction_signal') {
    return {
      bgColor: 'bg-[var(--go-warning)]/10',
      borderColor: 'border-[var(--go-warning)]/20',
      textColor: 'text-[var(--go-warning)]',
      icon: FiAlertCircle,
      iconBg: 'bg-[var(--go-warning)]',
      message: 'Dissatisfaction Signal: Heads 1-3 satisfied but does not meet full DISP criteria. Monitor under Consumer Duty.',
      confidence: confidencePct,
    };
  }

  return {
    bgColor: 'bg-background/70',
    borderColor: 'border-border/70',
    textColor: 'text-muted-foreground',
    icon: FiX,
    iconBg: 'bg-muted-foreground',
    message: 'Not Dissatisfaction: This does not meet the FCA complaint definition.',
    confidence: confidencePct,
  };
};

/**
 * Single head item component
 */
const HeadItem: React.FC<{
  head: FiveHeadsCriterion;
  index: number;
}> = ({ head, index }) => {
  const status = getStatusDisplay(head.status);
  const title = HEAD_TITLES[head.id] || head.label;

  return (
    <AccordionItem
      value={head.id}
      className={cn(
        'border rounded-lg overflow-hidden',
        status.borderColor
      )}
    >
      <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-muted/30 [&[data-state=open]]:bg-muted/30">
        <div className="flex items-center gap-3 text-left">
          {/* Status Icon */}
          <div
            className={cn(
              'flex items-center justify-center w-6 h-6 rounded-full shrink-0',
              status.bgColor
            )}
          >
            <IconWrapper
              icon={status.icon}
              className={cn('w-4 h-4', status.iconColor)}
            />
          </div>
          {/* Title */}
          <span className="text-sm font-semibold text-foreground">
            {index + 1}. {title}
          </span>
        </div>
      </AccordionTrigger>
      <AccordionContent className="px-4 pb-4 pt-0">
        <div className="pl-9">
          <p className="text-sm text-muted-foreground leading-relaxed">
            {head.explanation || `${title} ${head.status === 'detected' ? 'confirmed' : 'not confirmed'}.`}
          </p>
        </div>
      </AccordionContent>
    </AccordionItem>
  );
};

const FiveHeadComplaintTest = React.forwardRef<HTMLDivElement, FiveHeadComplaintTestProps>(
  ({ detection, isConfirmed, onConfirm, onReject, className, startCollapsed = true }, ref) => {
    const outcomeSummary = getOutcomeSummary(detection.outcome, detection.heads.length, detection.confidence);
    const satisfiedCount = detection.heads.filter(h => h.status === 'detected').length;

    // Start collapsed by default, or expanded if startCollapsed is false
    const defaultAccordionValue = startCollapsed ? [] : detection.heads.map(h => h.id);

    return (
      <div ref={ref} className={cn('flex flex-col gap-4', className)}>
        {/* Accordion of Heads */}
        <Accordion type="multiple" defaultValue={defaultAccordionValue} className="flex flex-col gap-2 mb-1">
          {detection.heads.map((head, index) => (
            <HeadItem key={head.id} head={head} index={index} />
          ))}
        </Accordion>

        {/* Outcome Summary Bar */}
        <div
          className={cn(
            'flex items-center gap-3 px-4 py-3 rounded-lg border',
            outcomeSummary.bgColor,
            outcomeSummary.borderColor
          )}
        >
          <div
            className={cn(
              'flex items-center justify-center w-5 h-5 rounded-full shrink-0',
              outcomeSummary.iconBg
            )}
          >
            <IconWrapper
              icon={outcomeSummary.icon}
              className="w-3 h-3 text-white"
            />
          </div>
          <div className="flex flex-col gap-0.5">
            <p className={cn('text-sm font-medium', outcomeSummary.textColor)}>
              {outcomeSummary.message}
            </p>
            <p className="text-xs text-muted-foreground">
              Model confidence: {outcomeSummary.confidence}
            </p>
          </div>
        </div>

        {/* Human Acknowledgment Actions */}
        <div className="flex items-center justify-end gap-2 border-t border-border pt-4">
          {isConfirmed ? (
            <Button
              variant="outline"
              size="sm"
              onClick={onReject}
              className="text-muted-foreground hover:text-destructive hover:border-destructive"
            >
              <IconWrapper icon={FiX} className="w-4 h-4 mr-2" />
              Not a Complaint
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={onConfirm}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              <IconWrapper icon={FiCheck} className="w-4 h-4 mr-2" />
              This is a Complaint
            </Button>
          )}
        </div>
      </div>
    );
  }
);

FiveHeadComplaintTest.displayName = 'FiveHeadComplaintTest';

export { FiveHeadComplaintTest };
